import React, { useMemo, useState } from 'react';
import { useBranches } from '../context/BranchContext';
import useAsync from '../hooks/useAsync';
import useStartBill from '../hooks/useStartBill';
import api from '../services/api';
import QueueCard from '../components/queue/QueueCard';
import BranchPickerModal from '../components/branch/BranchPickerModal';
import { EmptyState, Loader, Notice } from '../components/common/Feedback';
import { PlusIcon, RefreshIcon } from '../components/common/icons';
import { fmtDate } from '../utils/dates';

const QUEUE_POLL_MS = 20000;
const DONE = new Set(['finalized', 'delivered']);

const TABS = [
  { id: 'todo', label: 'To bill', test: (e) => !e.bill },
  { id: 'open', label: 'Billing', test: (e) => e.bill && !DONE.has(e.bill.status) },
  { id: 'done', label: 'Paid', test: (e) => e.bill && DONE.has(e.bill.status) },
  { id: 'all', label: 'All', test: () => true },
];

export default function Home() {
  const { param, selected, loading: branchesLoading } = useBranches();
  const [tab, setTab] = useState('todo');
  const queue = useAsync(() => api.queue(param), [param], {
    pollMs: QUEUE_POLL_MS,
    enabled: !branchesLoading,
  });
  const { start, busy, picking, pick, cancelPick } = useStartBill(() => queue.reload({ quiet: true }));

  const items = useMemo(() => queue.data?.items || [], [queue.data]);
  const counts = useMemo(() => Object.fromEntries(TABS.map((t) => [t.id, items.filter(t.test).length])), [items]);
  const shown = items.filter(TABS.find((t) => t.id === tab).test);
  const errors = Object.values(queue.data?.errors || {});

  return (
    <div className="dp-page">
      <div className="dp-page-head">
        <div>
          <p className="dp-eyebrow">{fmtDate(queue.data?.date || new Date().toISOString().slice(0, 10))}</p>
          <h1>Today&apos;s queue</h1>
          <p className="dp-help">Appointments from Demo Engagement and check-ins at the ticked branches.</p>
        </div>
        <div className="dp-head-actions">
          <button type="button" className="dp-btn dp-btn--ghost" onClick={() => queue.reload()} disabled={queue.loading}>
            <RefreshIcon size={16} /> Refresh
          </button>
          <button type="button" className="dp-btn" onClick={() => start({ kind: 'manual' })} disabled={busy}>
            <PlusIcon size={16} /> New walk-in bill
          </button>
        </div>
      </div>

      <div className="dp-stats">
        {TABS.slice(0, 3).map((t) => (
          <button key={t.id} type="button" className={`dp-stat${tab === t.id ? ' is-on' : ''}`} onClick={() => setTab(t.id)}>
            <span>{t.label}</span>
            <strong>{counts[t.id] || 0}</strong>
          </button>
        ))}
        <div className="dp-stat dp-stat--plain">
          <span>In queue</span>
          <strong>{items.length}</strong>
        </div>
      </div>

      {errors.map((msg) => (
        <Notice key={msg} tone="warn">
          {msg}
        </Notice>
      ))}
      {queue.error ? <Notice tone="error">{queue.error}</Notice> : null}

      <div className="dp-tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            className={`dp-tab${tab === t.id ? ' is-on' : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label} <span>{counts[t.id] || 0}</span>
          </button>
        ))}
      </div>

      {!selected.length ? (
        <EmptyState title="Nothing selected" body="Tick Demo Engagement or a branch in the Branches menu at the top." />
      ) : !queue.data && !queue.error ? (
        <Loader text="Loading today's queue…" />
      ) : shown.length ? (
        <div className="dp-queue-list">
          {shown.map((entry) => (
            <QueueCard key={entry.key} entry={entry} busy={busy} onStart={(e) => start({ kind: 'queue', entry: e })} />
          ))}
        </div>
      ) : (
        <EmptyState
          title={tab === 'todo' ? 'Everyone is billed' : 'Nothing here yet'}
          body={tab === 'todo' ? 'New appointments and check-ins show up here automatically.' : null}
        />
      )}

      {picking ? (
        <BranchPickerModal
          busy={busy}
          subtitle={picking.kind === 'queue' ? `${picking.entry.customer_name} · ${(picking.entry.services || []).map((s) => s.name).join(', ')}` : 'Walk-in customer'}
          onPick={pick}
          onClose={cancelPick}
        />
      ) : null}
    </div>
  );
}
